<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ResellerAPI from 'dashboard/api/reseller';
import Button from 'dashboard/components-next/button/Button.vue';
import ApplyTemplateDialog from './ApplyTemplateDialog.vue';
import ContractDetails from './ContractDetails.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const props = defineProps({
  subAccounts: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

// A árvore de sub-contas (até 3 níveis) vem inteira do backend; para carteiras
// grandes a tabela ficava quilométrica. Paginação no front preserva os níveis.
const ITEMS_PER_PAGE = 15;
const currentPage = ref(1);
const applyingTo = ref(null);
const filters = reactive({
  search: '',
  level: '',
  plan: '',
  mode: '',
  finance: '',
  since: '',
  until: '',
});
const filteredAccounts = computed(() =>
  props.subAccounts.filter(account => {
    const text = [
      account.name,
      account.referral?.indicator?.name,
      account.referral?.parent?.name,
    ]
      .join(' ')
      .toLocaleLowerCase();
    const started = (account.contract?.started_at || '').slice(0, 10);
    return (
      (!filters.search || text.includes(filters.search.toLocaleLowerCase())) &&
      (!filters.level || account.level === Number(filters.level)) &&
      (!filters.plan || account.contract?.plan === filters.plan) &&
      (!filters.mode || account.contract?.pricing_mode === filters.mode) &&
      (!filters.finance || account.finance_state === filters.finance) &&
      (!filters.since || (started && started >= filters.since)) &&
      (!filters.until || (started && started <= filters.until))
    );
  })
);
const contractPlans = computed(() => [
  ...new Set(props.subAccounts.map(a => a.contract?.plan).filter(Boolean)),
]);
const showPagination = computed(
  () => filteredAccounts.value.length > ITEMS_PER_PAGE
);
const pagedSubAccounts = computed(() =>
  filteredAccounts.value.slice(
    (currentPage.value - 1) * ITEMS_PER_PAGE,
    currentPage.value * ITEMS_PER_PAGE
  )
);
watch(filters, () => {
  currentPage.value = 1;
});
const clearFilters = () =>
  Object.keys(filters).forEach(key => {
    filters[key] = '';
  });
const referralText = account =>
  account.referral?.path?.map(item => item.name).join(' → ') || '';
// A lista carrega async e pode encolher (troca de parceiro/filtro) — volta pra
// página 1 pra nunca renderizar uma página vazia.
watch(
  () => props.subAccounts.length,
  () => {
    currentPage.value = 1;
  }
);

const { t } = useI18n();
const store = useStore();
const promotingId = ref(null);
const savingSubId = ref(null);
const amounts = reactive({});
const selectedPlanId = reactive({});

const plans = useMapGetter('reseller/getPlans');
const activePlans = computed(() => plans.value.filter(p => p.active));

// Revenda por comissão só ACOMPANHA a cobrança (nós cobramos o cliente):
// reenvia o boleto ou copia o PIX se o cliente pedir. Definir/alterar valor
// é exclusivo do parceiro white label (que fatura com o Asaas próprio).
const profile = useMapGetter('reseller/getProfile');
const isWhiteLabel = computed(
  () => profile.value?.partner_type === 'white_label'
);

const expandedId = ref(null);
const detailsDialog = ref(null);
const selectedAccount = computed(() =>
  props.subAccounts.find(a => a.id === expandedId.value)
);
const resendingId = ref(null);

const toggleDetails = account => {
  expandedId.value = account.id;
  detailsDialog.value.open();
};

const resendInvoice = async account => {
  resendingId.value = account.id;
  try {
    await ResellerAPI.resendInvoice(account.id);
    useAlert(t('REVENDA.SUB_ACCOUNTS.BILLING.RESENT'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('REVENDA.SUB_ACCOUNTS.BILLING.RESEND_ERROR')
    );
  } finally {
    resendingId.value = null;
  }
};

const copyPix = async account => {
  const pix = account.open_invoice?.pix_payload;
  if (!pix) return;
  await copyTextToClipboard(pix);
  useAlert(t('REVENDA.SUB_ACCOUNTS.BILLING.PIX_COPIED'));
};

const invoiceStatusLabel = status =>
  status === 'overdue'
    ? t('REVENDA.SUB_ACCOUNTS.BILLING.OPEN_OVERDUE')
    : t('REVENDA.SUB_ACCOUNTS.BILLING.OPEN_PENDING');

const invoiceStatusClass = status =>
  status === 'overdue'
    ? 'bg-n-ruby-3 text-n-ruby-11'
    : 'bg-n-amber-3 text-n-amber-11';

// Cotação local de um plano para uma conta, a partir do uso reportado no payload.
const quoteForAccount = (account, plan) => {
  const extraInbox = Math.max(
    0,
    (account.inboxes_count || 0) - plan.included_inboxes
  );
  const extraUser = Math.max(
    0,
    (account.users_count || 0) - plan.included_users
  );
  return (
    Number(plan.base_price) +
    extraInbox * Number(plan.price_per_inbox) +
    extraUser * Number(plan.price_per_user)
  );
};

const planFor = id => plans.value.find(p => p.id === Number(id));

onMounted(() => {
  store.dispatch('reseller/getPlans');
});

const formatDate = value =>
  value ? new Date(value).toLocaleDateString() : '—';

const formatMoney = value => `R$ ${Number(value).toFixed(2).replace('.', ',')}`;

const paymentStatusClass = status =>
  ({
    confirmed: 'bg-n-teal-3 text-n-teal-11',
    overdue: 'bg-n-ruby-3 text-n-ruby-11',
    refunded: 'bg-n-amber-3 text-n-amber-11',
    pending: 'bg-n-slate-3 text-n-slate-11',
  })[status] || 'bg-n-slate-3 text-n-slate-11';

const paymentStatusLabel = status =>
  ({
    confirmed: t('REVENDA.SUB_ACCOUNTS.PAYMENT_STATUS.CONFIRMED'),
    overdue: t('REVENDA.SUB_ACCOUNTS.PAYMENT_STATUS.OVERDUE'),
    refunded: t('REVENDA.SUB_ACCOUNTS.PAYMENT_STATUS.REFUNDED'),
    pending: t('REVENDA.SUB_ACCOUNTS.PAYMENT_STATUS.PENDING'),
  })[status] || status;

const promote = async account => {
  promotingId.value = account.id;
  try {
    await store.dispatch('reseller/promoteSubAccount', { id: account.id });
    useAlert(t('REVENDA.SUB_ACCOUNTS.PROMOTED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('REVENDA.SUB_ACCOUNTS.PROMOTE_ERROR')
    );
  } finally {
    promotingId.value = null;
  }
};

const setSubscription = async account => {
  const planId = selectedPlanId[account.id];
  const payload = { id: account.id };

  if (planId) {
    payload.resellerPlanId = planId;
  } else {
    const amount = Number(amounts[account.id]);
    if (!amount || amount <= 0) {
      useAlert(t('REVENDA.SUB_ACCOUNTS.SUBSCRIPTION.INVALID_AMOUNT'));
      return;
    }
    payload.amount = amount;
  }

  savingSubId.value = account.id;
  try {
    await store.dispatch('reseller/setSubscription', payload);
    useAlert(t('REVENDA.SUB_ACCOUNTS.SUBSCRIPTION.SAVED'));
    amounts[account.id] = '';
    selectedPlanId[account.id] = '';
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('REVENDA.SUB_ACCOUNTS.SUBSCRIPTION.ERROR')
    );
  } finally {
    savingSubId.value = null;
  }
};

const cancelSubscription = async account => {
  savingSubId.value = account.id;
  try {
    await store.dispatch('reseller/cancelSubscription', { id: account.id });
    useAlert(t('REVENDA.SUB_ACCOUNTS.SUBSCRIPTION.CANCELLED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('REVENDA.SUB_ACCOUNTS.SUBSCRIPTION.ERROR')
    );
  } finally {
    savingSubId.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col w-full gap-4">
    <h2 class="text-base font-medium text-n-slate-12">
      {{ t('REVENDA.SUB_ACCOUNTS.HEADER') }}
    </h2>

    <div class="grid grid-cols-2 gap-3 xl:grid-cols-4">
      <input
        v-model="filters.search"
        :placeholder="t('PORTFOLIO.SEARCH')"
        :aria-label="t('PORTFOLIO.SEARCH')"
        class="rounded-lg border border-n-weak bg-n-solid-1 p-2 text-sm"
      />
      <select
        v-model="filters.level"
        :aria-label="t('REVENDA.SUB_ACCOUNTS.LEVEL')"
        class="rounded-lg border border-n-weak bg-n-solid-1 p-2 text-sm"
      >
        <option value="">{{ t('PORTFOLIO.ALL_LEVELS') }}</option>
        <option v-for="level in [1, 2, 3]" :key="level" :value="level">
          {{ t('REVENDA.SUB_ACCOUNTS.LEVEL_N', { level }) }}
        </option>
      </select>
      <select
        v-model="filters.plan"
        :aria-label="t('PORTFOLIO.PLAN')"
        class="rounded-lg border border-n-weak bg-n-solid-1 p-2 text-sm"
      >
        <option value="">{{ t('PORTFOLIO.ALL_PLANS') }}</option>
        <option v-for="plan in contractPlans" :key="plan">{{ plan }}</option>
      </select>
      <select
        v-model="filters.mode"
        :aria-label="t('PORTFOLIO.MODE')"
        class="rounded-lg border border-n-weak bg-n-solid-1 p-2 text-sm"
      >
        <option value="">{{ t('PORTFOLIO.ALL_MODES') }}</option>
        <option
          v-for="mode in ['standard', 'all_inclusive', 'package']"
          :key="mode"
          :value="mode"
        >
          {{ t(`PORTFOLIO.${mode}`) }}
        </option>
      </select>
      <select
        v-model="filters.finance"
        :aria-label="t('PORTFOLIO.STATUS')"
        class="rounded-lg border border-n-weak bg-n-solid-1 p-2 text-sm"
      >
        <option value="">{{ t('PORTFOLIO.ALL_STATUSES') }}</option>
        <option
          v-for="status in ['pending', 'overdue', 'no_open_invoice']"
          :key="status"
          :value="status"
        >
          {{ t(`PORTFOLIO.${status}`) }}
        </option>
      </select>
      <label class="text-xs text-n-slate-11">
        {{ t('PORTFOLIO.SINCE') }}
        <input
          v-model="filters.since"
          type="date"
          class="block w-full rounded-lg border border-n-weak bg-n-solid-1 p-2"
        />
      </label>
      <label class="text-xs text-n-slate-11">
        {{ t('PORTFOLIO.UNTIL') }}
        <input
          v-model="filters.until"
          type="date"
          class="block w-full rounded-lg border border-n-weak bg-n-solid-1 p-2"
        />
      </label>
      <Button
        :label="t('PORTFOLIO.CLEAR')"
        variant="outline"
        @click="clearFilters"
      />
    </div>
    <p class="text-xs text-n-slate-10">
      {{
        t('PORTFOLIO.RESULTS', {
          count: filteredAccounts.length,
          total: subAccounts.length,
        })
      }}
    </p>
    <div v-if="isLoading" class="py-8 text-sm text-center text-n-slate-11">
      {{ t('REVENDA.SUB_ACCOUNTS.LOADING') }}
    </div>

    <div
      v-else-if="!subAccounts.length"
      class="py-8 text-sm text-center rounded-lg text-n-slate-11 bg-n-solid-2"
    >
      {{ t('REVENDA.SUB_ACCOUNTS.EMPTY') }}
    </div>

    <div v-else class="w-full max-w-full overflow-x-auto">
      <table class="w-full min-w-full divide-y divide-n-weak">
        <thead>
          <tr class="text-left text-n-slate-11">
            <th class="py-2 ltr:pr-4 rtl:pl-4 font-medium">
              {{ t('REVENDA.SUB_ACCOUNTS.NAME') }}
            </th>
            <th class="py-2 ltr:pr-4 rtl:pl-4 font-medium">
              {{ t('PORTFOLIO.PLAN') }}
            </th>
            <th class="py-2 ltr:pr-4 rtl:pl-4 font-medium">
              {{ t('REVENDA.SUB_ACCOUNTS.CREATED_AT') }}
            </th>
            <th class="py-2 ltr:pr-4 rtl:pl-4 font-medium">
              {{ t('REVENDA.SUB_ACCOUNTS.RESELLER') }}
            </th>
            <th class="py-2 ltr:pr-4 rtl:pl-4 font-medium">
              {{ t('REVENDA.SUB_ACCOUNTS.LEVEL') }}
            </th>
            <th class="w-px whitespace-nowrap py-2 font-medium">
              {{ t('REVENDA.SUB_ACCOUNTS.SUBSCRIPTION.HEADER') }}
            </th>
            <th class="w-px py-2" />
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-12">
          <template v-for="account in pagedSubAccounts" :key="account.id">
            <tr>
              <td class="py-3 ltr:pr-4 rtl:pl-4 font-medium">
                <button
                  class="text-left hover:text-n-blue-11"
                  @click="toggleDetails(account)"
                >
                  {{ account.name }}
                </button>
              </td>
              <td class="py-3 ltr:pr-4 rtl:pl-4 text-n-slate-11">
                <span class="block">{{
                  account.contract?.plan || t('PORTFOLIO.NO_CONTRACT')
                }}</span>
                <span v-if="account.contract" class="block text-xs">
                  {{ formatMoney(account.contract.total) }} ·
                  {{ t('PORTFOLIO.CURRENT_CALCULATION') }} ·
                  {{ t(`PORTFOLIO.${account.contract.status}`) }}
                </span>
              </td>
              <td class="py-3 ltr:pr-4 rtl:pl-4 text-n-slate-11">
                {{ formatDate(account.created_at) }}
              </td>
              <td class="py-3 ltr:pr-4 rtl:pl-4">
                <span
                  v-if="account.reseller"
                  class="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
                >
                  {{ t('REVENDA.SUB_ACCOUNTS.IS_RESELLER') }}
                </span>
                <Button
                  v-else
                  variant="ghost"
                  size="sm"
                  :label="t('REVENDA.SUB_ACCOUNTS.MAKE_RESELLER')"
                  :is-loading="promotingId === account.id"
                  @click="promote(account)"
                />
                <Button
                  v-if="(account.level || 1) === 1"
                  variant="ghost"
                  size="sm"
                  :label="t('REVENDA.TEMPLATES.APPLY')"
                  @click="applyingTo = account"
                />
              </td>
              <td class="py-3 ltr:pr-4 rtl:pl-4">
                <button
                  v-tooltip.bottom="referralText(account)"
                  class="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                  @click="toggleDetails(account)"
                >
                  {{
                    t('REVENDA.SUB_ACCOUNTS.LEVEL_N', {
                      level: account.level || 1,
                    })
                  }}
                  <span class="i-lucide-info" />
                </button>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{
                    account.referral?.indicator?.name ||
                    t('PORTFOLIO.NO_INDICATOR')
                  }}
                </p>
                <p
                  v-if="account.referral?.mismatch"
                  class="text-xs text-n-amber-10"
                >
                  {{ t('PORTFOLIO.REFERRAL_REVIEW') }}
                </p>
              </td>
              <!-- Comissão: só ACOMPANHA a cobrança (reenviar/copiar PIX). -->
              <td v-if="!isWhiteLabel" class="py-3">
                <p
                  v-if="
                    account.open_invoice &&
                    account.contract &&
                    Number(account.open_invoice.amount) !==
                      Number(account.contract.total)
                  "
                  class="mb-1 max-w-64 text-xs text-n-amber-10"
                >
                  {{ t('PORTFOLIO.DIFFERENCE') }}
                </p>
                <div
                  v-if="account.open_invoice"
                  class="flex items-center gap-2 whitespace-nowrap"
                >
                  <span
                    class="inline-flex items-center rounded-full px-2 py-0.5 text-xs"
                    :class="invoiceStatusClass(account.open_invoice.status)"
                  >
                    {{ invoiceStatusLabel(account.open_invoice.status) }}
                  </span>
                  <span class="text-xs font-medium text-n-slate-12">
                    {{ formatMoney(account.open_invoice.amount) }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{
                      t('REVENDA.SUB_ACCOUNTS.BILLING.DUE', {
                        date: formatDate(account.open_invoice.due_date),
                      })
                    }}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-send"
                    :label="t('REVENDA.SUB_ACCOUNTS.BILLING.RESEND')"
                    :is-loading="resendingId === account.id"
                    @click="resendInvoice(account)"
                  />
                  <Button
                    v-if="account.open_invoice.pix_payload"
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-copy"
                    :label="t('REVENDA.SUB_ACCOUNTS.BILLING.COPY_PIX')"
                    @click="copyPix(account)"
                  />
                </div>
                <span v-else class="whitespace-nowrap text-xs text-n-slate-10">
                  {{ t('REVENDA.SUB_ACCOUNTS.BILLING.PLATFORM') }}
                </span>
              </td>
              <td v-else class="py-3">
                <div
                  v-if="account.subscription_active"
                  class="flex items-center gap-2"
                >
                  <span class="text-xs text-n-teal-11">
                    {{
                      t('REVENDA.SUB_ACCOUNTS.SUBSCRIPTION.PER_MONTH', {
                        amount: formatMoney(account.subscription_amount),
                      })
                    }}
                  </span>
                  <span
                    v-if="account.payment_status"
                    class="inline-flex items-center px-2 py-0.5 text-xs rounded-full"
                    :class="paymentStatusClass(account.payment_status)"
                  >
                    {{ paymentStatusLabel(account.payment_status) }}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    color="ruby"
                    :label="t('REVENDA.SUB_ACCOUNTS.SUBSCRIPTION.CANCEL')"
                    :is-loading="savingSubId === account.id"
                    @click="cancelSubscription(account)"
                  />
                </div>
                <!-- reset-base: sai do estilo global de form (senão o input vira
                   largura total h-10 e estoura a célula da tabela) -->
                <div v-else class="flex items-center gap-2 whitespace-nowrap">
                  <select
                    v-if="activePlans.length"
                    v-model="selectedPlanId[account.id]"
                    class="reset-base max-w-36 rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-xs text-n-slate-12"
                  >
                    <option value="">
                      {{ t('REVENDA.SUB_ACCOUNTS.SUBSCRIPTION.CUSTOM') }}
                    </option>
                    <option
                      v-for="plan in activePlans"
                      :key="plan.id"
                      :value="plan.id"
                    >
                      {{ plan.name }}
                    </option>
                  </select>
                  <span
                    v-if="selectedPlanId[account.id]"
                    class="text-xs font-medium text-n-slate-12 whitespace-nowrap"
                  >
                    {{
                      formatMoney(
                        quoteForAccount(
                          account,
                          planFor(selectedPlanId[account.id])
                        )
                      )
                    }}
                  </span>
                  <input
                    v-else
                    v-model="amounts[account.id]"
                    type="number"
                    min="1"
                    step="0.01"
                    :placeholder="t('REVENDA.SUB_ACCOUNTS.SUBSCRIPTION.AMOUNT')"
                    class="reset-base w-28 rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-xs text-n-slate-12"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    :label="t('REVENDA.SUB_ACCOUNTS.SUBSCRIPTION.CHARGE')"
                    :is-loading="savingSubId === account.id"
                    @click="setSubscription(account)"
                  />
                </div>
              </td>
              <td class="py-3 ltr:pl-2 rtl:pr-2">
                <Button
                  variant="ghost"
                  size="sm"
                  :icon="
                    expandedId === account.id
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                  :label="t('REVENDA.SUB_ACCOUNTS.DETAILS.BUTTON')"
                  @click="toggleDetails(account)"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <Dialog
      ref="detailsDialog"
      :title="selectedAccount?.name || ''"
      width="5xl"
      :show-confirm-button="false"
      :show-cancel-button="false"
      overflow-y-auto
      @close="expandedId = null"
    >
      <ContractDetails
        v-if="selectedAccount"
        :key="selectedAccount.id"
        :account="selectedAccount"
      />
    </Dialog>

    <PaginationFooter
      v-if="!isLoading && showPagination"
      :current-page="currentPage"
      :total-items="filteredAccounts.length"
      :items-per-page="ITEMS_PER_PAGE"
      @update:current-page="currentPage = $event"
    />
    <ApplyTemplateDialog
      v-if="applyingTo"
      :sub-account="applyingTo"
      @close="applyingTo = null"
    />
  </div>
</template>
